import { ScopeContext, useScope } from 'dreamstate';
import { default as throttle } from 'lodash.throttle';
import React, {
  Dispatch,
  ElementType,
  memo,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { rem, Flex, Text, IconButton, SystemStyleObject } from '@vimeo/bokeh';
import { CloseXSmall, GripHorizontalLine } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Id, Maybe, Optional } from '@vimeo/core/types/generic';

import { browserConfig } from '../../configs/BrowserConfig';
import { useSwipe } from '../../hooks/dom/useSwipe';
import { usePreviousValue } from '../../hooks/react/usePreviousValue';
import { useStateTransmorphic } from '../../hooks/react/useStateTransmorphic';
import { useScrollbarStyles } from '../../hooks/styling/useScrollbarStyles';
import { ELiveSignal, TLivePanelChangeRequestSignalEvent } from '../../store/signals';
import { CallablePlaceholder } from '../../types/general';
import { createDomName } from '../../utils/dom/identifying';
import { LiveErrorBoundary } from '../error/boundary';
import {
  createExtendButtonStyles,
  createSidebarStyles,
  SWIPE_OFFSET_FOR_ACTION,
} from './ResponsiveSidebar.styles';
import {
  ESidebarAttach,
  IResponsiveSidebarContext,
  ISidebarItemDescriptor,
  TConfigProp,
} from './ResponsiveSidebar.types';
import {
  ResponsiveSidebarContext,
  useSidebarPropConfigValue,
  useSidebarSwipeDirection,
} from './ResponsiveSidebar.utils';
import { ResponsiveSidebarPanelItem } from './ResponsiveSidebarPanelItem';

export interface ISidebarProps extends IBaseDomElementProps {
  attach: TConfigProp<ESidebarAttach>;
  state?: [Optional<Id>, Dispatch<SetStateAction<Optional<Id>>>];
  initialStateValue?: Optional<Id>;
  /**
   * Whether should enable hash navigation.
   */
  isWithRouting?: boolean;
  /**
   * Whether inactive state/closing is disabled.
   */
  isInactiveDisabled?: boolean;
  /**
   * Setting fullscreen expansion. If true maxSize will be ignored
   */
  isFullscreen?: boolean;
  /**
   * Represents screen orientation changes
   */
  isPortrait?: boolean;
  /**
   * Is header enabled in panel content.
   */
  isWithHeader?: boolean;
  /**
   * Whether list of items should take all size of the panel.
   * Will be centered otherwise.
   */
  isFullSizeItemList?: boolean;
  /**
   * Whether size of panel items should adjust in responsive way.
   * When height is below some threshold, icons will be smaller with tooltips instead of large view.
   */
  isResponsive?: boolean;
  /**
   * Breakpoint to switch on, when responsive is enabled.
   */
  responsiveBreakpoint?: number;
  /**
   * Maximum height/width for expanded state
   */
  maxSize?: TConfigProp<number>;
  /**
   * Is with border panel content or not.
   */
  isWithBorder?: boolean;
  isLoading?: boolean;
  items: Array<ISidebarItemDescriptor>;
  contentNotice?: ReactNode;
  contentFooter?: ReactNode;
  zIndex?: number | string;
  backgroundColor?: string;
  onOpen?: (active: Optional<Id>) => void;
  onClose?: (active: Optional<Id>) => void;
  onToggleExtend?: (isExtended: boolean) => void;
  onActiveItemChangedCallback?: (item: Optional<Id>) => void;
}

export const ResponsiveSidebar = memo(function ({
  id = createDomName('sidebar'),
  className = createDomName('sidebar'),
  attach,
  state,
  initialStateValue = null,
  isFullscreen = false,
  isFullSizeItemList = false,
  isPortrait = false,
  isResponsive = false,
  isWithRouting = false,
  isWithHeader = true,
  isInactiveDisabled = false,
  isWithBorder = false,
  maxSize,
  responsiveBreakpoint = 0,
  isLoading = false,
  items,
  contentFooter,
  contentNotice,
  zIndex,
  backgroundColor,
  onOpen,
  onClose,
  onToggleExtend,
  onActiveItemChangedCallback = CallablePlaceholder,
}: ISidebarProps) {
  const scope: ScopeContext = useScope();
  const [activeItemId, setActiveItemId] = useStateTransmorphic<Optional<Id>>(
    state,
    initialStateValue,
  );
  const [isHeaderEnabled, setIsHeaderEnabled] = useState<boolean>(isWithHeader);
  const [isExtended, setIsExtended] = useState<boolean>(false);
  const [isMinified, setIsMinified] = useState<boolean>(
    isResponsive ? window.innerHeight < responsiveBreakpoint : false,
  );

  useEffect(() => {
    setActiveItemId(initialStateValue);
  }, [initialStateValue, setActiveItemId]);

  const buttonRef: RefObject<HTMLButtonElement | null> = useRef(null);

  const { direction, offset } = useSwipe({
    ref: buttonRef,
    dependencies: [activeItemId],
  });

  const isActive: boolean = useMemo(() => Boolean(activeItemId), [activeItemId]);
  const sidebarAttach: ESidebarAttach = useSidebarPropConfigValue<ESidebarAttach>(attach);
  const sidebarIsFullscreen: boolean = useSidebarPropConfigValue<boolean>(isFullscreen);
  const sidebarMaxSize: Maybe<number> = useSidebarPropConfigValue<Maybe<number>>(maxSize);

  const { swipeSameDirection, swipeOppositeDirection } = useSidebarSwipeDirection(
    sidebarAttach,
    isPortrait,
  );

  const onActiveItemChanged = useCallback(
    (value: Optional<Id>) => {
      onActiveItemChangedCallback(value);
      setActiveItemId(value);
      setIsHeaderEnabled(isWithHeader);

      const nextItem: Maybe<ISidebarItemDescriptor> = items.find((it) => it.id === value);

      if (isWithRouting) {
        window.location.hash = nextItem?.hash ? nextItem.hash : (window.location.hash = 'none');
      }
    },
    [items, isWithRouting, isWithHeader, setActiveItemId, onActiveItemChangedCallback],
  );

  const onTogglePanel = useCallback(
    (itemId: string) => () => {
      setIsHeaderEnabled(isWithHeader);

      if (activeItemId === itemId) {
        // Do not allow closing.
        if (isInactiveDisabled) {
          return;
        } else {
          onClose?.(activeItemId);
          onToggleExtend?.(false);
          setIsExtended(false);
          onActiveItemChanged(null);
        }
      } else {
        onOpen?.(itemId);
        onActiveItemChanged(itemId);
      }
    },
    [
      isWithHeader,
      activeItemId,
      isInactiveDisabled,
      onClose,
      onToggleExtend,
      onActiveItemChanged,
      onOpen,
    ],
  );

  const onToggleExtendPanel = useCallback(() => {
    setIsExtended((isExtended) => {
      const newIsExtended = !isExtended;

      onToggleExtend?.(newIsExtended);

      return newIsExtended;
    });
  }, [onToggleExtend]);

  const responsiveSidebarContext: IResponsiveSidebarContext = useMemo(
    () => ({
      isHeaderEnabled,
      setIsHeaderEnabled,
    }),
    [isHeaderEnabled],
  );

  const activeItem: Maybe<ISidebarItemDescriptor> = useMemo(
    () => (Boolean(activeItemId) ? items.find(({ id }) => id === activeItemId) : null),
    [activeItemId, items],
  );

  const activeItemContent: ReactNode = useMemo(() => {
    if (activeItem) {
      const { id: itemId, header, headerBackground, headerMenu, isClosable, Content } = activeItem;
      const ItemContent: ElementType = Content as ElementType;

      return (
        <>
          {contentNotice ? contentNotice : null}

          {isHeaderEnabled ? (
            <Flex
              id={createDomName(id, 'content-header', itemId as Id)}
              className={createDomName(className, 'content-header', itemId as Id)}
              justifyContent={'space-between'}
              alignItems={'center'}
              padding={rem(16)}
              background={headerBackground}
            >
              <Text
                id={createDomName(id, 'header')}
                className={createDomName(className, 'header')}
                variant={'heading-sm'}
                color={'text-primary'}
              >
                {header}
              </Text>

              <Flex
                id={createDomName(id, 'header-menu')}
                className={createDomName(className, 'header-menu')}
                justifyContent={'flex-end'}
                alignItems={'center'}
                gap={rem(4)}
              >
                {headerMenu}

                {isClosable && !isInactiveDisabled ? (
                  <CloseXSmall
                    id={createDomName(id, 'close-sidebar-button')}
                    className={createDomName(className, 'close-sidebar-button')}
                    cursor={'pointer'}
                    tabIndex={0}
                    aria-label={'Close panel'}
                    onClick={onTogglePanel(itemId as string)}
                  />
                ) : null}
              </Flex>
            </Flex>
          ) : null}

          <LiveErrorBoundary key={itemId} additionalInfo={itemId} component={'ResponsiveSidebar'}>
            <ItemContent
              id={createDomName(id, 'item-content', itemId as Id)}
              className={createDomName(className, 'item-content')}
            />
          </LiveErrorBoundary>
        </>
      );
    } else {
      return null;
    }
  }, [activeItem, className, contentNotice, id, isHeaderEnabled]);

  const isHorizontalLayering: boolean =
    attach === ESidebarAttach.TOP || attach === ESidebarAttach.BOTTOM;

  const scrollbarStyles: SystemStyleObject = useScrollbarStyles();

  const panel: ReactNode = useMemo(() => {
    if (Object.keys(items).length) {
      return (
        <Flex
          id={createDomName(id, 'panel')}
          className={createDomName(className, 'panel')}
          position={'relative'}
          shrink={0}
          justifyContent={'space-between'}
          boxShadow={'none'}
          zIndex={101}
          color={'text-primary'}
          background={backgroundColor || 'background'}
        >
          <Flex
            id={createDomName(id, 'panel-scroller')}
            className={createDomName(className, 'panel-scroller')}
            justifyContent={'flex-start'}
            gap={rem(4)}
            padding={rem(8)}
            overflow={'hidden'}
            grow={isFullSizeItemList ? 1 : undefined}
            sx={{
              ...scrollbarStyles,

              '&::-webkit-scrollbar': {
                width: rem(12),
              },

              '&::-webkit-scrollbar-thumb': {
                ...scrollbarStyles['&::-webkit-scrollbar-thumb'],
                borderRadius: rem(12),
              },
            }}
          >
            {items.map(({ id: itemId, icon, label }, index) => {
              if (!itemId) {
                return (
                  <Flex
                    key={index}
                    className={createDomName(id, 'panel-break')}
                    transition={'120ms ease-in-out'}
                    height={isHorizontalLayering ? '75%' : undefined}
                    width={isHorizontalLayering ? undefined : '75%'}
                    margin={isHorizontalLayering ? 'auto 0' : '0 auto'}
                    borderLeft={isHorizontalLayering ? '1px solid' : undefined}
                    borderTop={isHorizontalLayering ? undefined : '1px solid'}
                    borderColor={'stroke'}
                  />
                );
              }

              return (
                <ResponsiveSidebarPanelItem
                  id={createDomName(id, 'panel-button', itemId as Id)}
                  className={createDomName(id, 'panel-button')}
                  key={itemId}
                  icon={icon}
                  label={label}
                  attach={sidebarAttach}
                  isMinified={isMinified}
                  isActive={itemId === activeItemId}
                  isMobile={browserConfig.BROWSER?.isMobile as boolean}
                  onClick={onTogglePanel(itemId as string)}
                >
                  {label}
                </ResponsiveSidebarPanelItem>
              );
            })}

            <Flex grow={1} />

            {contentFooter}
          </Flex>
        </Flex>
      );
    } else {
      return null;
    }
  }, [
    items,
    id,
    className,
    isFullSizeItemList,
    scrollbarStyles,
    contentFooter,
    sidebarAttach,
    isMinified,
    activeItemId,
    onTogglePanel,
    isHorizontalLayering,
  ]);

  const isRenderPanelBefore: boolean = useMemo(
    () =>
      sidebarAttach === ESidebarAttach.TOP ||
      (sidebarAttach === ESidebarAttach.LEFT && !isPortrait),
    [sidebarAttach, isPortrait],
  );

  const isRenderPanelAfter: boolean = useMemo(
    () =>
      sidebarAttach === ESidebarAttach.RIGHT ||
      sidebarAttach === ESidebarAttach.BOTTOM ||
      (sidebarAttach === ESidebarAttach.LEFT && isPortrait),
    [sidebarAttach, isPortrait],
  );

  const prevIsPortrait: boolean = usePreviousValue<boolean>(isPortrait);

  useEffect(() => {
    if (direction === swipeOppositeDirection && offset > SWIPE_OFFSET_FOR_ACTION && !isExtended) {
      setIsExtended(true);
      onToggleExtend?.(true);
    } else if (direction === swipeSameDirection && offset > SWIPE_OFFSET_FOR_ACTION && isExtended) {
      setIsExtended(false);
      onToggleExtend?.(false);
    }
  }, [direction, offset, swipeOppositeDirection, swipeSameDirection, isExtended, onToggleExtend]);

  // Handle changes of state from scope.
  useEffect(() => {
    scope.subscribeToSignals((signalEvent: TLivePanelChangeRequestSignalEvent) => {
      if (signalEvent.type === ELiveSignal.LIVE_PANEL_TAB_CHANGE_REQUEST) {
        if (signalEvent.data === null || items.find((it) => it.id === signalEvent.data)) {
          onActiveItemChanged(signalEvent.data);
        }
      }
    });
  }, [scope, items, onActiveItemChanged]);

  // Do not allow empty state if disabled.
  useLayoutEffect(() => {
    if (isInactiveDisabled) {
      if (items.length === 0) {
        return;
      } else if (!activeItemId || !items.some((it) => it.id === activeItemId)) {
        return setActiveItemId(items[0].id as Id);
      }
    }
  }, [isInactiveDisabled, activeItemId, items, setActiveItemId]);

  // Check if minified breakpoints are respected. Currently works with vertical only.
  useEffect(() => {
    if (
      !isResponsive ||
      attach === ESidebarAttach.TOP ||
      attach === ESidebarAttach.BOTTOM ||
      isPortrait
    ) {
      return setIsMinified(false);
    }

    const onResize = throttle(() => {
      setIsMinified(window.innerHeight < responsiveBreakpoint);
    }, 500);

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [attach, isPortrait, isResponsive, responsiveBreakpoint]);

  if (Object.keys(items).length) {
    return (
      <ResponsiveSidebarContext.Provider value={responsiveSidebarContext}>
        <Flex
          id={id}
          className={className}
          shrink={0}
          background={backgroundColor || 'background'}
          sx={createSidebarStyles({
            id,
            className,
            zIndex: zIndex,
            attach: sidebarAttach,
            isActive: isActive,
            isFullscreen: sidebarIsFullscreen,
            isPortrait: isPortrait,
            isExtended: isExtended,
            isMobile: browserConfig.BROWSER?.isMobile as boolean,
            isMinified: isMinified,
            isWithBorder: isWithBorder,
            isOrientationChanged: prevIsPortrait !== isPortrait,
            maxSize: sidebarMaxSize,
            backgroundColor,
          })}
        >
          {isRenderPanelBefore ? panel : null}

          <Flex
            id={createDomName(id, 'content')}
            className={createDomName(className, 'content')}
            position={'relative'}
            width={'100%'}
            height={'100%'}
            maxWidth={(browserConfig.BROWSER?.isMobile as boolean) ? '100%' : rem(640)}
            zIndex={100}
          >
            <Flex
              id={createDomName(id, 'content-wrapper')}
              className={createDomName(className, 'content-wrapper')}
              direction={'column'}
              position={'absolute'}
              color={'text-primary'}
            >
              {isLoading ? null : activeItemContent}
            </Flex>

            {activeItemContent ? (
              <IconButton
                ref={buttonRef}
                aria-label={'extend'}
                id={createDomName(id, 'content-grip')}
                className={createDomName(className, 'content-grip')}
                icon={<GripHorizontalLine />}
                position={'absolute'}
                transition={'none'}
                width={rem(80)}
                variant={'hyperminimal'}
                display={'none'}
                _active={{
                  transform: undefined,
                  background: 'transparent',
                }}
                _focus={{
                  background: 'transparent',
                }}
                sx={createExtendButtonStyles({
                  isMobile: browserConfig.BROWSER?.isMobile as boolean,
                  attach: sidebarAttach,
                  isPortrait: isPortrait,
                  isFullscreen: sidebarIsFullscreen,
                })}
                onClick={onToggleExtendPanel}
              />
            ) : null}
          </Flex>

          {isRenderPanelAfter ? panel : null}
        </Flex>
      </ResponsiveSidebarContext.Provider>
    );
  } else {
    return null;
  }
});
